import type { KeyboardEvent } from "react";

// Shared rather than repeated per page: the composers would otherwise each decide their
// own shortcut, and drift.

// Mid-composition (an IME), Enter confirms the characters rather than sending. Safari
// reports that Enter with isComposing false, but keyCode 229.
const composing = (event: KeyboardEvent<HTMLTextAreaElement>): boolean =>
  event.nativeEvent.isComposing || event.keyCode === 229;

// A phone keyboard has no Shift+Enter, so there Enter stays a new line and the button sends.
const touch = (): boolean => window.matchMedia("(pointer: coarse)").matches;

/** A conversation box: Enter sends, Shift+Enter is a new line, as in a chat tool.
 * Cmd+Enter (Ctrl+Enter) also sends. */
export const sendOnEnter =
  (send: () => void) =>
  (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== "Enter" || event.shiftKey || composing(event) || touch()) return;
    event.preventDefault();
    send();
  };

/** A box whose send starts something costly (Run now's note): Enter is a new line, and
 * only Cmd+Enter (Ctrl+Enter) sends, so a line break never launches it. */
export const sendOnCmdEnter =
  (send: () => void) =>
  (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== "Enter" || !(event.metaKey || event.ctrlKey) || composing(event)) return;
    event.preventDefault();
    send();
  };
